<template>
  <Radio.Group
    v-bind="attrs"
    v-model:value="state"
    :class="{ 'mts-radio-button-group': isSingleBtn }"
    :buttonStyle="buttonStyle"
  >
    <template v-for="item in getOptions" :key="`${item.key}`">
      <Radio.Button
        v-if="isBtn || isSingleBtn"
        :value="item.value"
        :disabled="item.disabled"
        @click="handleClick(item)"
      >
        <MIcon v-if="item.icon" class="align--1px mr-4px" :icon="item.icon" />
        {{ item.label }}
      </Radio.Button>
      <Radio
        v-else
        :value="item.value"
        :class="block ? 'w-full !leading-[2]' : ''"
        :disabled="item.disabled"
        @click="handleClick(item)"
      >
        {{ item.label }}
      </Radio>
    </template>
  </Radio.Group>
</template>
<script lang="ts" setup>
  import { Radio } from 'ant-design-vue';
  import { get, isEqual } from 'lodash-es';
  import { computed, type PropType, ref, unref, watch } from 'vue';
  import type { Fn, Option as BaseOption, Recordable } from '@mts-fe/types';
  import { useAttrs, useRuleFormItem } from '@mts-fe/hooks';
  import { isArray, isFunction, omit, propTypes } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';
  import { useCountMap } from '../hooks/useCountMap';

  defineOptions({ name: 'ApiRadioGroup', inheritAttrs: false });

  type RawValue = string | number | boolean;
  interface Option extends Omit<BaseOption, 'value'> {
    value: RawValue;
  }
  const props = defineProps({
    value: { type: [String, Number, Boolean] as PropType<RawValue> },
    api: {
      type: Function as PropType<(arg?: any) => Promise<Option[] | Recordable<any>>>,
      default: null,
    },
    params: {
      type: [Object, String] as PropType<any | string>,
      default: () => ({}),
    },
    countApi: {
      type: [String] as PropType<string>,
    },
    isSingleBtn: propTypes.bool.def(false),
    isBtn: propTypes.bool.def(false),
    buttonStyle: {
      type: [String] as PropType<'solid' | 'outline'>,
      default: 'solid',
    },
    block: propTypes.bool.def(false),
    resultField: propTypes.string.def(''),
    labelField: propTypes.string.def('label'),
    valueField: propTypes.string.def('value'),
    keyField: propTypes.string.def('key'),
    immediate: propTypes.bool.def(true),
    numberToString: propTypes.bool.def(true),
    useFirstValue: propTypes.bool.def(true),
    prependAll: propTypes.bool.def(false),
    options: {
      type: Array<Option>,
      default: [],
    },
    beforeFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
    afterFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
  });

  const emit = defineEmits(['options-change', 'change', 'update:value']);

  const optionsRef = ref<Option[]>([]);

  const loading = ref(false);
  const emitData = ref<Option[]>([]);
  const attrs = useAttrs();
  // Embedded in the form, just use the hook binding to perform form verification
  const [state] = useRuleFormItem(props, 'value', 'change', emitData);

  // Processing options value
  const getOptions = computed(() => {
    const { labelField, valueField, keyField, numberToString } = props;

    const data = unref(optionsRef).reduce((prev, next: any) => {
      if (next) {
        const value = get(next, valueField);
        prev.push({
          ...omit(next, [labelField, valueField]),
          key: get(next, keyField) || value,
          label: get(next, labelField) + getCountNum(value),
          value: numberToString ? `${value}` : value,
        });
      }
      return prev;
    }, [] as Option[]);
    return data.length > 0 ? data : props.options;
  });
  const { getCountNum } = useCountMap(props.countApi, getOptions, attrs.value.codeField);

  watch(
    () => state.value,
    () => {
      emit('update:value', state.value);
    },
  );

  watch(
    () => props.params,
    (value, oldValue) => {
      if (isEqual(value, oldValue)) return;
      fetch();
    },
    { deep: true, immediate: props.immediate },
  );

  async function fetch() {
    let { api, beforeFetch, afterFetch, params, resultField } = props;
    if (!api || !isFunction(api)) return;
    optionsRef.value = [];
    try {
      loading.value = true;
      if (beforeFetch && isFunction(beforeFetch)) {
        const res = await beforeFetch(params);
        if (typeof res === 'boolean' && !res) return;
        params = res || params;
      }
      let res = await api(params);
      if (afterFetch && isFunction(afterFetch)) {
        res = (await afterFetch(res)) || res;
      }
      if (isArray(res)) {
        optionsRef.value = [...res];
        emitChange();
        return;
      }
      if (resultField) {
        optionsRef.value = get(res, resultField) || [];
      }
      emitChange();
    } catch (error) {
      console.warn(error);
    } finally {
      loading.value = false;
    }
  }

  function emitChange() {
    props.prependAll && optionsRef.value.unshift({ value: '', label: t('g.25090009') });
    if (props.useFirstValue && !state.value) {
      state.value = unref(getOptions)[0]?.value;
      handleClick(unref(getOptions)[0]);
    }

    emit('options-change', unref(getOptions));
  }

  function handleClick(...args) {
    emitData.value = args;
  }
</script>

<style lang="less">
  .mts-radio-button-group.ant-radio-group {
    display: flex;
    flex-flow: row wrap;
    gap: 8px;

    > .ant-radio-button-wrapper {
      border: 1px solid #d5d9e1;
      border-radius: 4px;

      &::before {
        display: none;
      }

      &-checked:not(.ant-radio-button-wrapper-disabled) {
        border-color: #01a887;
      }
    }
  }
</style>
